import { ReactNode, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { Input } from "sr/helpers";
import { LevelBar } from "sr/helpers/ui-components/LevelBar";
import { Button } from "sr/helpers/ui-components/company/Button";
import ContentWithSpinner from "sr/layout/common/ContentWithSpiner";
import { useValidationErrorHandle } from "sr/helpers/functions/validationErrorHandle";
import { scrollToError } from "sr/helpers/functions/scrollToError";
import { getUserDetails } from "sr/helpers/functions/getUserDetails";
import CheckBoxHandle from "sr/helpers/functions/CheckBoxHandle";
import { CheckBox } from "sr/helpers/ui-components/company/CheckBox";
import DropDown from "sr/helpers/ui-components/DropDown.tsx";
import { ReactDatePicker } from "sr/helpers/ui-components/ReactDatePicker";
import { RequirementCommonHeader } from "../layout/RequirementCommonHeader";
import { serviceTypes } from "./RequirementJsonData";
import { getBusinessType } from "sr/utils/api/masterData/getBusinessType";
import { getAreaType } from "sr/utils/api/masterData/getAreaType";
import { getDuration } from "sr/utils/api/masterData/getDuration";
import { getFrequency } from "sr/utils/api/masterData/getFrequency";
import { LoaderFullScreen } from "sr/layout/common/LoaderFullScreen";
import { post } from "sr/utils/axios";
import { useSelector } from "react-redux";
import ApiResponse from "sr/models/ApiResponse";
import { toast } from "react-toastify";
import { RequirementBasicSchema } from "sr/utils/schemas/corporate/requirements/RequirementBasicSchema";

const toISODate = (d: Date | string) => {
  try {
    const date = typeof d === "string" ? new Date(d) : d;
    if (isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const calculateEndDate = (startDate: string, duration: string): string | null => {
  if (!startDate || !duration) return null;

  const start = new Date(startDate);
  if (isNaN(start.getTime())) return null;

  const parts = duration.split(" "); 
  if (parts.length < 2) return null;

  const num = parseInt(parts[0], 10);
  const unit = parts[1].toLowerCase();
  if (isNaN(num)) return null;

  const end = new Date(start);

  switch (unit) {
    case "month":
    case "months":
      end.setMonth(end.getMonth() + num);
      break;
    case "year":
    case "years":
      end.setFullYear(end.getFullYear() + num);
      break;
    default:
      return null;
  }

  return end.toISOString();
};

type BasicValues = {
  title: string;
  description: string;
  business_types: string[];
  service_types: string[];
  area: string;
  area_type: string;
  frequency: string;
  duration: string;
  start_date: string;
  budget: string;
};

export default function RequirementBasic() {
  const intl = useIntl();
  const navigate = useNavigate();
  const validationErrorHandle = useValidationErrorHandle();
  const [loader, setLoader] = useState<"" | "submit">("");
  const [businessTypeOptions, setBusinessTypeOptions] = useState<{value:string; label:string}[]>([]);
  const [areaTypeOptions, setAreaTypeOptions] = useState<{value:string; label:string}[]>([]);
  const [frequencyOptions, setFrequencyOptions] = useState<{value:string; label:string}[]>([]);
  const [durationOptions, setDurationOptions] = useState<{value:string; label:string}[]>([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const corporateData = useSelector((state: any) => state.corporate?.data);
  const user = getUserDetails();

  const initialValues: BasicValues = {
    title: "",
    description: "",
    business_types: [],
    service_types: [],
    area: "",
    area_type: "",
    frequency: "",
    duration: "",
    start_date: toISODate(new Date()),
    budget: "",
  };

  const {
    errors,
    submitForm,
    values,
    setFieldValue,
    handleChange,
    touched,
  } = useFormik<BasicValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema: RequirementBasicSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (vals) => {
      setLoader("submit");
      try {
        const payload = {
          user_id: user?.user_id,
          corporate_id: corporateData?.id,
          title: vals.title?.trim(),
          description: vals.description?.trim(),
          special_instructions: "",
          business_types: vals.business_types,
          region: "",
          service_type: vals.service_types,
          area: vals.area,
          area_type: vals.area_type,
          frequency: vals.frequency,
          duration: vals.duration,
          start_date: vals.start_date ? new Date(vals.start_date).toISOString() : null,
          end_date: calculateEndDate(vals.start_date, vals.duration),
          budget: String(vals.budget || '').replace(/[^0-9.]/g, ''),
          status: "open",
          total_biddings: 0,
          documents_required: [],
          attachments: [],
        };

        const res = await post<ApiResponse<any>>("/corporate/requirements", payload);

        if (res?.success) {
          const reqId = res.data?.id;
          if (reqId) {
            navigate(`/corporate/requirement-post/advanced/${reqId}`);
          }
        } else {
          toast.error(res?.error?.message || intl.formatMessage({ id: 'API.ERROR.REQUIREMENT.CREATE', defaultMessage: 'Failed to create requirement' }));
        }
      } catch (e) {
        toast.error(intl.formatMessage({ id: 'API.ERROR.GENERIC.TRY_AGAIN', defaultMessage: 'Something went wrong, please try again' }));
      } finally {
        setLoader("");
      }
    },
  });

  useEffect(() => {
    scrollToError(errors);
  }, [errors]);

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const res = await getBusinessType();
        if (res?.success && Array.isArray(res.data)) {
          const mapped = res.data.map((item: any) => ({
            value: item.value,
            label: item.label,
          }));
          setBusinessTypeOptions(mapped);
        }
      } catch {}
      try {
        const res = await getAreaType();
        if (res?.success && Array.isArray(res.data)) {
          const mapped = res.data.map((item: any) => ({
            value: item.value,
            label: item.label,
          }));
          setAreaTypeOptions(mapped);
        }
      } catch {}
      try {
        const res = await getFrequency();
        if (res?.success && Array.isArray(res.data)) {
          const mapped = res.data.map((item: any) => ({
            value: item.value,
            label: item.label,
          }));
          setFrequencyOptions(mapped);
        }
      } catch {}
      try {
        const res = await getDuration();
        if (res?.success && Array.isArray(res.data)) {
          const mapped = res.data.map((item: any) => ({
            value: item.value,
            label: item.label,
          }));
          setDurationOptions(mapped);
        }
      } catch {}
    };
    fetchMasterData();
  }, []);

  const errorMsg = (name: keyof BasicValues) =>
    ((touched as any)[name] || submitAttempted) && (errors as any)[name] ? (
      <p className="text-sm text-red-500">
        {validationErrorHandle((errors as any)[name] as ReactNode)}
      </p>
    ) : null;

  return (
    <div>
      {loader === "submit" && <LoaderFullScreen />}
      <ContentWithSpinner loader={false} height="h-screen">
        <div className="container mx-auto max-w-4xl py-8 px-4">
          <RequirementCommonHeader
            title={
              <FormattedMessage
                id="CORPORATE.REQUIREMENT.POST.TITLE"
                defaultMessage="Basic Details"
              />
            }
          />
          <div className="bg-white rounded-2xl shadow p-8">
            <LevelBar progress={1} total={3} />
            {(() => { return null })()}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setSubmitAttempted(true);
                submitForm();
              }}
              className="space-y-10 mt-8"
              noValidate
            >
              <Input
                label={<FormattedMessage id="CORPORATE.REQUIREMENT.POST.LABEL.TITLE" defaultMessage="Title" />}
                name="title"
                value={values.title}
                onChange={handleChange}
                error={errors.title}
                placeholder={intl.formatMessage({ id: 'CORPORATE.REQUIREMENT.POST.PLACEHOLDER.TITLE', defaultMessage: 'Enter requirement title' })}
              />

              <Input
                label={<FormattedMessage id="CORPORATE.REQUIREMENT.POST.LABEL.DESCRIPTION" defaultMessage="Description" />}
                name="description"
                value={values.description}
                onChange={handleChange}
                error={errors.description}
                type="textarea"
                placeholder={intl.formatMessage({ id: 'CORPORATE.REQUIREMENT.POST.PLACEHOLDER.DESCRIPTION', defaultMessage: 'Describe your requirement in detail' })}
              />

              <div>
                <p className="pt-2 text-base">
                  <FormattedMessage id="CORPORATE.REQUIREMENT.POST.BUSINESSTYPE" defaultMessage="Business Types" />
                </p>
                <div className="flex flex-wrap gap-4 mt-2">
                  {(businessTypeOptions.length ? businessTypeOptions : []).map((bt) => (
                    <CheckBox
                      key={bt.value}
                      value={bt.value}
                      checked={values.business_types.includes(bt.value)}
                      onChange={(e) =>
                        CheckBoxHandle(
                          e,
                          values.business_types,
                          (v) => setFieldValue("business_types", v)
                        )
                      }
                      label={<FormattedMessage id={bt.label} defaultMessage={bt.label} />}
                    />
                  ))}
                </div>
                {errorMsg("business_types")}
              </div>

              <div>
                <p className="pt-2 text-base">
                  <FormattedMessage id="CORPORATE.REQUIREMENT.POST.SERVICETYPE" defaultMessage="Service Types" />
                </p>
                <div className="flex flex-wrap gap-4 mt-2">
                  {serviceTypes.map((s) => (
                    <CheckBox
                      key={s.value}
                      value={s.value}
                      checked={values.service_types.includes(s.value)}
                      onChange={(e) =>
                        CheckBoxHandle(
                          e,
                          values.service_types,
                          (v) => setFieldValue("service_types", v)
                        )
                      }
                      label={<FormattedMessage id={s.label} defaultMessage={s.label} />}
                    />
                  ))}
                </div>
                {errorMsg("service_types")}
              </div>

              <Input
                label={<FormattedMessage id="CORPORATE.REQUIREMENT.POST.AREA" defaultMessage="Area" />}
                name="area"
                value={values.area}
                onChange={handleChange}
                error={errors.area}
                placeholder={intl.formatMessage({ id: 'CORPORATE.REQUIREMENT.POST.PLACEHOLDER.AREA', defaultMessage: 'e.g., Cafeteria - 200 seats' })}
              />

              <DropDown
                error={errors.area_type as any}
                onChange={(e) => setFieldValue("area_type", e.target.value)}
                value={values.area_type}
                name="area_type"
                label={<FormattedMessage id="CORPORATE.REQUIREMENT.POST.AREATYPE" defaultMessage="Area Type" />}
                className="h-11 mt-2 border border-zinc-800 rounded-lg"
              >
                <option value="">
                  <FormattedMessage id="COMMON.SELECT" defaultMessage="Select an option" />
                </option>
                {areaTypeOptions.map((a) => (
                  <option key={a.value} value={a.value}>
                    <FormattedMessage id={a.label} defaultMessage={a.label} />
                  </option>
                ))}
              </DropDown>
              

              <DropDown
                error={errors.frequency as any}
                onChange={(e) => setFieldValue("frequency", e.target.value)}
                value={values.frequency}
                name="frequency"
                label={<FormattedMessage id="CORPORATE.REQUIREMENT.POST.FREQUENCY" defaultMessage="Frequency" />}
                className="h-11 mt-2 border border-zinc-800 rounded-lg"
              >
                <option value="">
                  <FormattedMessage id="COMMON.SELECT" defaultMessage="Select an option" />
                </option>
                {frequencyOptions.map((f) => (
                  <option key={f.value} value={f.value}>
                    <FormattedMessage id={f.label} defaultMessage={f.label} />
                  </option>
                ))}
              </DropDown>
              

              <DropDown
                error={errors.duration as any}
                onChange={(e) => setFieldValue("duration", e.target.value)}
                value={values.duration}
                name="duration"
                label={<FormattedMessage id="CORPORATE.REQUIREMENT.POST.DURATION" defaultMessage="Duration" />}
                className="h-11 mt-2 border border-zinc-800 rounded-lg"
              >
                <option value="">
                  <FormattedMessage id="COMMON.SELECT" defaultMessage="Select an option" />
                </option>
                {durationOptions.map((d) => (
                  <option key={d.value} value={d.value}>
                    <FormattedMessage id={d.label} defaultMessage={d.label} />
                  </option>
                ))}
              </DropDown>
              

              <ReactDatePicker
                label={<FormattedMessage id="CORPORATE.REQUIREMENT.POST.STARTDATE" defaultMessage="Start Date" />}
                inputClass="w-full bg-white border border-zinc-800 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-blue-500 focus:border-[#0078ae] focus:shadow-[0_3px_0px_0px_rgb(37,87,167)] p-2 uppercase"
                selected={values.start_date ? new Date(values.start_date) : null}
                onChange={(date) => setFieldValue("start_date", date ? toISODate(date as Date) : "")}
                error={errors.start_date}
                minDate={(() => { const d = new Date(); d.setHours(0,0,0,0); return d })()}
              />
              

              <Input
                label={<FormattedMessage id="CORPORATE.REQUIREMENT.POST.BUDGET" defaultMessage="Budget / Estimated Spend" />}
                name="budget"
                value={values.budget}
                onChange={(e) => {
                  const raw = e.target.value as string;
                  const sanitized = raw.replace(/[^0-9.]/g, '');
                  const parts = sanitized.split('.');
                  const intPart = parts[0] || '';
                  const decPart = parts[1] !== undefined ? parts[1].slice(0, 2) : undefined;
                  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                  const formatted = decPart !== undefined ? `${withCommas}.${decPart}` : withCommas;
                  setFieldValue('budget', formatted);
                }}
                error={errors.budget}
                type="text"
                placeholder={intl.formatMessage({ id: 'CORPORATE.REQUIREMENT.POST.PLACEHOLDER.BUDGET', defaultMessage: 'e.g., $12,00,000' })}
              />
              

              <div className="flex justify-end">
                <Button
                  text={<FormattedMessage id="RECRUITER.POSTJOB.CONTINUE.BUTTON" defaultMessage="Continue" />}
                />
              </div>
            </form>
          </div>
        </div>
      </ContentWithSpinner>
    </div>
  );
}
